import { inject } from '@angular/core';
import {
  provideRouter,
  Route,
  Router,
  Routes,
  UrlSegment,
  withPreloading,
  withRouterConfig
} from '@angular/router';

import { brandGuard } from '@amfam/shared/utility/brand/lib/guards/brand.guard';
import { optimizelyGuard } from '@amfam/shared/utility/feature-flag/data-access/lib/guards/optimizely.guard';
import { FeatureFlagService } from '@amfam/shared/utility/feature-flag/data-access/lib/services/feature-flag.service';
import { EventDispatcherService } from '@amfam/shared/utility/shared-services/lib/event-bus/event-dispatcher.service';
import { loadingSpinnerResolver } from '@amfam/ui-kit/lib/loading-spinner/services/loading-spinner-resolver.service';
import { ComponentViewService } from '@amfam/ui-kit/lib/shared-components/component-view.service';

import { agentCookieGuard } from './core/agent-cookie/agent-cookie.guard';
import { forgerockAuthGuard } from './core/auth/forgerock-auth.guard';
import { featureFlagGuard } from './core/feature-flag/feature-flag.guard';
import { maintenanceGuard } from './core/maintenance/maintenance.guard';
import { navigationResolver } from './core/navigation/navigation-resolver.service';
import { Error404Routes } from './error/error.routes';
import { LoginRoutes } from './login/login.routes';
import { PreloadSelectedModules } from './selective-preload-strategy';

const appRoutes: Routes = [
  // redirect the empty route
  {
    path: '',
    redirectTo: 'login',
    pathMatch: 'full'
  },
  ...LoginRoutes,
  ...Error404Routes,
  {
    path: 'enroll',
    canActivate: [maintenanceGuard],
    loadChildren: () => import('./enrollment/enrollment.routes').then(m => m.EnrollmentRoutes),
    data: {
      preload: true
    }
  },
  {
    path: 'quick-pay',
    canActivate: [
      () => {
        const featureFlagService = inject(FeatureFlagService);
        if (featureFlagService.isEnabled('guest_pay_ui')) {
          return true;
        }
        const router = inject(Router);
        router.navigate(['/login']);
        return false;
      }
    ],
    loadChildren: () =>
      import('@amfam/billing/quick-pay/feature/lib/quick-pay.routes').then(m => m.QUICK_PAY_ROUTES),
    data: { preload: false }
  },
  {
    path: 'outage',
    loadComponent: () => import('./core/outage/outage.component').then(m => m.OutageComponent)
  },
  {
    path: 'maintenance',
    loadComponent: () =>
      import('./core/maintenance/maintenance.component').then(m => m.MaintenanceComponent)
  },
  {
    path: 'delete-myaccount-confirmation',
    loadComponent: () =>
      import('@amfam/profile/feature/lib/components/delete-my-account-confirmation/delete-my-account-confirmation.component').then(
        m => m.DeleteMyAccountConfirmationComponent
      )
  },
  {
    path: '',
    loadChildren: () => import('./account/account.routes').then(m => m.AccountRoutes),
    canActivate: [forgerockAuthGuard, maintenanceGuard],
    canActivateChild: [agentCookieGuard],
    canMatch: [
      (route: Route, segments: UrlSegment[]) => {
        const routes = segments.map(s => s.path);
        const eventDispatcher = inject(EventDispatcherService);
        if (routes.includes(ComponentViewService.WEBVIEW_URL_PART))
          eventDispatcher.showHeaders(false);
        return true;
      }
    ],
    resolve: {
      partner: navigationResolver
    },
    data: {
      redirectRoute: '/login',
      preload: true
    }
  }
];

const allRoutes: Routes = [
  {
    path: '',
    canActivate: [brandGuard, optimizelyGuard],
    resolve: {
      spinner: loadingSpinnerResolver
    },
    children: [
      ...appRoutes,
      {
        path: ':expid',
        children: [...appRoutes],
        canActivate: [featureFlagGuard],
        data: {
          feature: 'partner',
          redirectRoute: '/not-found',
          preload: true
        }
      },
      // this one always last
      { path: '**', redirectTo: 'not-found', pathMatch: 'full' }
    ]
  }
];

export const appRoutingProviders = [
  provideRouter(
    allRoutes,
    withRouterConfig({ paramsInheritanceStrategy: 'always', onSameUrlNavigation: 'reload' }),
    withPreloading(PreloadSelectedModules)
  )
];
